import React, { MouseEventHandler, useRef } from 'react';
import { isIOS } from 'react-device-detect';

import { getIconByName } from '../../../MuiIcon/Icon';
import { DetailWidgetInteractionItem, StyleProps } from '../WidgetElement';
import { sendInteractGATrackEventData } from '../../../../util/gAUtil';
import { getBorderStyle, getDropShadowStyle } from '../util';
import { useWidgetAnimation } from '../../../Player/hooks/useWidgetAnimation';

type GenerateIconStyleProps = {
	isHover: boolean;
	clickable: boolean;
	style: StyleProps;
};

const generateIconStyle = ({ style, clickable, isHover }: GenerateIconStyleProps) => {
	const {
		textLabel: { color, hoverColor },
	} = style;
	const { border, hoverBorder } = getBorderStyle(style.backgroundSettings);
	const { dropShadow, hoverDropShadow } = getDropShadowStyle(style.transformSettings);

	return {
		color: isHover ? (hoverColor || color) : color,
		width: '100%',
		height: '100%',
		border: isHover ? (hoverBorder || border) : border,
		filter: "drop-shadow(" + (isHover ? (hoverDropShadow || dropShadow) : dropShadow) + ")",
		cursor: clickable ? 'pointer' : 'default',
	};
};

const Icon = ({
	id,
	style,
	data,
	clickable,
	isGoToSceneClick,
	isHover,
	widgetRef,
	isShowWidget,
	isExiting,
	onExitDone,
	exitTiming,
	onClickExitStart,
	onClick,
	handleMouseEnter,
	handleMouseLeave,
	scenarioId,
	sceneId,
}: DetailWidgetInteractionItem) => {
	const {
		coordinate: { x, y },
		dimension: { w = 150, h = 100 },
		rotation,
	} = style;

	const {
		settings: { iconKey = 'AcUnit' },
		name,
		type,
	} = data;

	const gElementRef = useRef<SVGGElement | null>();
	// the icon <svg> itself - its `fill` flashes to the active colour on press
	const bgElementRef = useRef<SVGSVGElement | null>();
	const { triggerClickAnimation } = useWidgetAnimation({ gElementRef, bgElementRef, style, isShowWidget, isExiting, onExitDone, exitTiming, onClickExitStart, clickable });

	const handleWidgetAction = (event: React.TouchEvent | React.MouseEvent | React.KeyboardEvent) => {
		event.stopPropagation();
		event.persist?.();
		sendInteractGATrackEventData(type, name, scenarioId, sceneId);
		triggerClickAnimation(() => onClick(event));
	};

	const handleTouchStart = (event: React.TouchEvent) => {
		event.preventDefault();
		handleWidgetAction(event);
	};

	const handleOnKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			handleWidgetAction(event);
		}
	}

	return (<g style={{ transform: `rotate(${rotation ?? 0}deg)` }}>
		<g
			id={id}
			onClick={handleWidgetAction}
			onTouchStart={isIOS ? handleTouchStart : undefined}
			onMouseEnter={handleMouseEnter as MouseEventHandler<SVGGElement>}
			onMouseLeave={handleMouseLeave as MouseEventHandler<SVGGElement>}
			onKeyDown={handleOnKeyDown}
			tabIndex={0}
			role='button'
			aria-label={name}
			ref={r => gElementRef.current = r}
		>
			<foreignObject
				aria-hidden="true"
				ref={widgetRef}
				x={x}
				y={y}
				width={w}
				height={h}
			>
				<div style={{ width: '100%', height: '100%' }} aria-hidden="true">
					{getIconByName(iconKey, {
						ref: (r: SVGSVGElement | null) => (bgElementRef.current = r),
						style: generateIconStyle({ clickable, style, isHover }),
					})}
				</div>
			</foreignObject>
		</g>
	</g>);
};

export default Icon;
